import { useEffect, useState } from "react";
import { API } from "aws-amplify";
import { message } from "antd";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import LoadableComp from "../../../commonComponents/LoadableComp/LoadableComp";
import CurrentQuoteCard from "./components/CurrentQuoteCard";
import QuoteHistoryTable from "./components/QuoteHistoryTable";
import BlocklistCard from "./components/BlocklistCard";
import dayjsTZ from "../../../../utils/dayjs";
import {
  QOTD_FIELD_ID,
  QUOTE_HISTORY_FIELD_ID,
  QUOTE_BLOCKLIST_FIELD_ID,
} from "../../../../constantsFactory/quoteOfTheDay";
import "./SistemiCitimiDites.scss";

// Reviews the daily quote. The quotes themselves come from public collections
// and are chosen each morning by the quoteOfTheDay Lambda, so there is nothing
// to author here — only to inspect, re-roll, and rule out.
function SistemiCitimiDites() {
  const { isDarkMode } = useSelector((state) => state.usersList);

  const [current, setCurrent] = useState({});
  const [history, setHistory] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchAll = async () => {
    try {
      const [currentRes, historyRes, blockedRes] = await Promise.all([
        API.get("programFields", `/programFields/${QOTD_FIELD_ID}`),
        API.get("programFields", `/programFields/${QUOTE_HISTORY_FIELD_ID}`),
        API.get("programFields", `/programFields/${QUOTE_BLOCKLIST_FIELD_ID}`),
      ]);
      setCurrent(currentRes?.fieldOptions || {});
      setHistory(
        Array.isArray(historyRes?.fieldOptions) ? historyRes.fieldOptions : []
      );
      setBlocked(
        Array.isArray(blockedRes?.fieldOptions) ? blockedRes.fieldOptions : []
      );
    } catch (err) {
      console.log("Error loading quote of the day: ", err);
      message.error("Nuk u ngarkuan dot të dhënat e citimit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Every write replaces the whole fieldOptions array, so callers hand over the
  // full list they want stored.
  const saveBlocked = async (next, successText) => {
    const previous = blocked;
    setBlocked(next);
    try {
      await API.put(
        "programFields",
        `/programFields/${QUOTE_BLOCKLIST_FIELD_ID}`,
        { body: { fieldOptions: next } }
      );
      message.success(successText);
      return true;
    } catch (err) {
      console.log("Error saving blocklist: ", err);
      setBlocked(previous);
      message.error("Ndodhi një gabim gjatë ruajtjes");
      return false;
    }
  };

  const generate = async ({ silent = false } = {}) => {
    setGenerating(true);
    try {
      const res = await API.post("quoteOfTheDay", "/quoteOfTheDay/generate", {
        body: { force: true },
      });
      if (res?.changed) {
        if (!silent) message.success("Citimi i ditës u rifreskua");
        await fetchAll();
      } else if (!silent) {
        message.warning(
          res?.reason === "no_corpus"
            ? "Burimi i citimeve nuk u arrit"
            : "Citimi nuk u ndryshua"
        );
      }
    } catch (err) {
      console.log("Error generating quote: ", err);
      message.error("Gjenerimi dështoi");
    } finally {
      setGenerating(false);
    }
  };

  // The current record stores the text but not the corpus id, so it is matched
  // through today's history entry.
  const currentQuoteId = () =>
    history.find((h) => h?.date === current?.date)?.quoteId || null;

  const blockQuote = async (entry) => {
    if (!entry?.quoteId) {
      message.warning("Ky citim nuk mund të bllokohet");
      return;
    }
    if (blocked.some((b) => b?.quoteId === entry.quoteId)) {
      message.info("Ky citim është bllokuar tashmë");
      return;
    }
    const saved = await saveBlocked(
      [
        {
          id: uuidv4(),
          type: "quote",
          quoteId: entry.quoteId,
          quote: entry.quote || "",
          author: entry.author || "",
          date: dayjsTZ().format("DD/MM/YYYY"),
        },
        ...blocked,
      ],
      "Citimi u bllokua"
    );
    // Only re-roll when the blocked line is the one currently on the header.
    if (saved && entry.quoteId === currentQuoteId()) await generate({ silent: true });
  };

  const blockAuthor = async (name) => {
    const author = String(name || "").trim();
    if (!author) return;
    if (
      blocked.some(
        (b) =>
          b?.type === "author" &&
          (b?.author || "").toLowerCase() === author.toLowerCase()
      )
    ) {
      message.info("Ky autor është bllokuar tashmë");
      return;
    }
    const saved = await saveBlocked(
      [
        {
          id: uuidv4(),
          type: "author",
          author,
          date: dayjsTZ().format("DD/MM/YYYY"),
        },
        ...blocked,
      ],
      "Autori u bllokua"
    );
    if (
      saved &&
      (current?.author || "").toLowerCase() === author.toLowerCase()
    ) {
      await generate({ silent: true });
    }
  };

  const removeBlocked = (row) =>
    saveBlocked(
      blocked.filter((b) => b?.id !== row?.id),
      "U hoq nga lista"
    );

  return (
    <LoadableComp loading={loading}>
      <div className="globalPageWrapper">
        <div className={`citimiDitesPage ${isDarkMode ? "dark" : ""}`}>
          <CurrentQuoteCard
            {...{
              current,
              generating,
              isDarkMode,
              onGenerate: generate,
              onBlockQuote: () =>
                blockQuote({
                  quoteId: currentQuoteId(),
                  quote: current?.quote,
                  author: current?.author,
                }),
              onBlockAuthor: () => blockAuthor(current?.author),
              onEdited: fetchAll,
            }}
          />

          <QuoteHistoryTable
            {...{ history, isDarkMode, onBlockQuote: blockQuote }}
          />

          <BlocklistCard
            {...{
              blocked,
              isDarkMode,
              onBlockAuthor: blockAuthor,
              onRemove: removeBlocked,
            }}
          />
        </div>
      </div>
    </LoadableComp>
  );
}

export default SistemiCitimiDites;
